import type { Table } from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  FileText,
  CheckCircle2,
  ArrowUpDown,
  MoreHorizontal,
} from 'lucide-react';
import React, { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  isSelected?: boolean;
  ActionSelectComponent?: (items: TData[]) => React.ReactNode;
}

// Memoize pagination component to prevent unnecessary re-renders
export function DataTablePagination<TData>({
  table,
  isSelected,
  ActionSelectComponent,
}: DataTablePaginationProps<TData>) {
  const rowSelectionData = table
    .getFilteredSelectedRowModel()
    ?.rows?.map((x: any) => x?.original);

  const { pageSize, pageIndex } = table.getState().pagination;
  const pageCount = table.getPageCount();
  const canPreviousPage = table.getCanPreviousPage();
  const canNextPage = table.getCanNextPage();
  const filteredRowsLength = table.getFilteredRowModel().rows.length;
  const selectedRowsLength = table.getFilteredSelectedRowModel().rows.length;

  // Calculate current page range
  const currentPageStart = pageIndex * pageSize + 1;
  const currentPageEnd = Math.min((pageIndex + 1) * pageSize, filteredRowsLength);
  
  // Calculate progress
  const progressPercentage = pageCount > 0 ? ((pageIndex + 1) / pageCount) * 100 : 0;

  // Generate page numbers to display
  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 5;
    const halfVisible = Math.floor(maxVisible / 2);
    
    let start = Math.max(0, pageIndex - halfVisible);
    let end = Math.min(pageCount - 1, pageIndex + halfVisible);
    
    // Adjust if we're near the beginning or end
    if (pageIndex <= halfVisible) {
      end = Math.min(pageCount - 1, maxVisible - 1);
    } else if (pageIndex >= pageCount - halfVisible - 1) {
      start = Math.max(0, pageCount - maxVisible);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    return pages;
  }, [pageIndex, pageCount]);

  return (
    <div className="relative mt-2 overflow-hidden rounded-xl border border-border/50 bg-gradient-to-r from-background via-muted/5 to-background p-3 shadow-sm">
      {/* Progress Bar */}
      <div className="absolute top-0 left-0 right-0 h-0.5">
        <Progress value={progressPercentage} className="h-full bg-muted/30">
          <div 
            className="h-full bg-gradient-to-r from-primary/80 to-primary rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          />
        </Progress>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Left Section - Selection Info & Actions */}
        <div className="flex flex-1 items-center gap-2">
          {isSelected && ActionSelectComponent && rowSelectionData?.length !== 0 && (
            <div className="flex items-center gap-2">
              {ActionSelectComponent(rowSelectionData)}
            </div>
          )}

          <div className="flex items-center gap-2">
            {/* Selection Counter */}
            {selectedRowsLength > 0 && (
              <Badge 
                variant="outline" 
                className={cn(
                  "h-7 px-2.5 gap-1.5 transition-all duration-200",
                  "bg-gradient-to-r from-emerald-50 to-emerald-100/50",
                  "dark:from-emerald-950/30 dark:to-emerald-900/20",
                  "border-emerald-300 dark:border-emerald-800",
                  "text-emerald-700 dark:text-emerald-400"
                )}
              >
                <CheckCircle2 className="h-3 w-3" />
                <span className="font-semibold">{selectedRowsLength}</span>
                <span className="text-xs opacity-70">محدد</span>
              </Badge>
            )}

            {/* Total Items Badge */}
            <Badge 
              variant="outline" 
              className={cn(
                "h-7 px-2.5 gap-1.5",
                "bg-gradient-to-r from-blue-50 to-blue-100/50",
                "dark:from-blue-950/30 dark:to-blue-900/20",
                "border-blue-300 dark:border-blue-800",
                "text-blue-700 dark:text-blue-400"
              )}
            >
              <FileText className="h-3 w-3" />
              <span className="font-semibold">{filteredRowsLength}</span>
              <span className="text-xs opacity-70">عنصر</span>
            </Badge>

            {/* Current Page Range */}
            <Badge 
              variant="outline" 
              className={cn(
                "h-7 px-2.5 gap-1.5",
                "bg-muted/50 border-border/50",
                "text-muted-foreground"
              )}
            >
              <span className="text-xs">
                عرض {currentPageStart}-{currentPageEnd} من {filteredRowsLength}
              </span>
            </Badge>
          </div>
        </div>

        {/* Right Section - Pagination Controls */}
        <div className="flex items-center gap-2">
          {/* Page Size Selector */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground hidden sm:inline">عرض:</span>
            <Select
              value={`${pageSize}`}
              onValueChange={(value) => table.setPageSize(Number(value))}
            >
              <SelectTrigger className={cn(
                "h-7 w-[65px] text-xs",
                "bg-background/80 border-border/50",
                "hover:bg-background hover:border-primary/30",
                "focus:ring-1 focus:ring-primary/30",
                "transition-all duration-200"
              )}>
                <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent 
                side="top" 
                className="min-w-[70px] bg-background/95 backdrop-blur-sm border-border/50"
              >
                {[10, 20, 30, 40, 50, 100].map((size) => (
                  <SelectItem 
                    key={size} 
                    value={`${size}`} 
                    className="text-xs hover:bg-primary/10 hover:text-primary cursor-pointer"
                  >
                    {size} صف
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Pagination Buttons Container */}
          <div className={cn(
            "flex items-center gap-1 p-1",
            "bg-muted/30 rounded-lg",
            "border border-border/30"
          )}>
            <TooltipProvider>
              {/* First Page Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-7 w-7 rounded-md",
                      "hover:bg-primary/10 hover:text-primary",
                      "disabled:opacity-30 disabled:hover:bg-transparent",
                      "transition-all duration-200"
                    )}
                    onClick={() => table.setPageIndex(0)}
                    disabled={!canPreviousPage}
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  الصفحة الأولى
                </TooltipContent>
              </Tooltip>

              {/* Previous Page Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-7 w-7 rounded-md",
                      "hover:bg-primary/10 hover:text-primary",
                      "disabled:opacity-30 disabled:hover:bg-transparent",
                      "transition-all duration-200"
                    )}
                    onClick={() => table.previousPage()}
                    disabled={!canPreviousPage}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  الصفحة السابقة
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Page Numbers */}
            <div className="hidden sm:flex items-center gap-1 px-1">
              {pageNumbers[0] > 0 && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-xs hover:bg-primary/10 hover:text-primary"
                    onClick={() => table.setPageIndex(0)}
                  >
                    1
                  </Button>
                  {pageNumbers[0] > 1 && (
                    <span className="px-1 text-muted-foreground">
                      <MoreHorizontal className="h-3 w-3" />
                    </span>
                  )}
                </>
              )}
              
              {pageNumbers.map(pageNum => (
                <Button
                  key={pageNum}
                  variant={pageNum === pageIndex ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "h-7 w-7 p-0 text-xs transition-all duration-200",
                    pageNum === pageIndex 
                      ? "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90"
                      : "hover:bg-primary/10 hover:text-primary"
                  )}
                  onClick={() => table.setPageIndex(pageNum)}
                >
                  {pageNum + 1}
                </Button>
              ))}
              
              {pageNumbers[pageNumbers.length - 1] < pageCount - 1 && (
                <>
                  {pageNumbers[pageNumbers.length - 1] < pageCount - 2 && (
                    <span className="px-1 text-muted-foreground">
                      <MoreHorizontal className="h-3 w-3" />
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-xs hover:bg-primary/10 hover:text-primary"
                    onClick={() => table.setPageIndex(pageCount - 1)}
                  >
                    {pageCount}
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Page Indicator */}
            <div className={cn(
              "flex sm:hidden items-center justify-center px-3 h-7",
              "bg-gradient-to-r from-primary/10 to-primary/5",
              "rounded-md border border-primary/20",
              "text-xs font-medium text-primary"
            )}>
              {pageIndex + 1} / {pageCount}
            </div>

            <TooltipProvider>
              {/* Next Page Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-7 w-7 rounded-md",
                      "hover:bg-primary/10 hover:text-primary",
                      "disabled:opacity-30 disabled:hover:bg-transparent",
                      "transition-all duration-200"
                    )}
                    onClick={() => table.nextPage()}
                    disabled={!canNextPage}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  الصفحة التالية
                </TooltipContent>
              </Tooltip>

              {/* Last Page Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-7 w-7 rounded-md",
                      "hover:bg-primary/10 hover:text-primary",
                      "disabled:opacity-30 disabled:hover:bg-transparent",
                      "transition-all duration-200"
                    )}
                    onClick={() => table.setPageIndex(pageCount - 1)}
                    disabled={!canNextPage}
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  الصفحة الأخيرة
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </div>
  );
}